import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import { StatusBlock } from "@components/ui/blocks/Status";
import { useRef, useState } from "react";
import { SignatureRequest } from "types";
import { signatureRequestsApi } from "@services/api/signatureRequests";
import { showNotification } from "context/notification.service";
import { useDataStore } from "@store/user_org";
import { isGUEST } from "@helpers/accessHelpers";

export const SignatureStatusModal = ({
  onClose,
  signatureRequest,
}: {
  onClose: () => void;
  signatureRequest: SignatureRequest;
}) => {
  const userData = useDataStore((s) => s.userData);
  const isGuest = isGUEST(userData);
  const [resendingId, setResendingId] = useState<string | null>(null);
  // Synchronous re-entry lock. React state updates aren't immediate, so
  // a rapid double-click can slip past `disabled={resendingId === ...}`
  // in the render that hasn't committed yet. A ref flips instantly.
  const resendingRef = useRef<Set<string>>(new Set());

  const handleResend = async (signerId: string) => {
    if (resendingRef.current.has(signerId)) return;
    resendingRef.current.add(signerId);
    setResendingId(signerId);
    try {
      await signatureRequestsApi.resend(signatureRequest.id, signerId);
      showNotification({
        type: "success",
        message: "Lien de signature renvoyé",
      });
    } catch {
      showNotification({
        type: "error",
        message: "Erreur lors de l'envoi",
      });
    } finally {
      resendingRef.current.delete(signerId);
      setResendingId(null);
    }
  };

  const isSigned = (status: string) =>
    status === "signed" || status === "signed_in_app" || status === "signed_remote";
  const signedCount = signatureRequest.signers.filter((s) => isSigned(s.status)).length;
  const totalCount = signatureRequest.signers.length;

  return (
    <BaseModal
      title="Suivi de signature"
      startIcon={<Icon1 name="sign" size={20} />}
      onClose={onClose}
      width="max-w-3xl"
    >
      <div className="flex flex-col gap-4 p-4">
        {/* Document info */}
        <div className="flex flex-col gap-1">
          <div className="text-h2 font-semibold">
            {signatureRequest.document_name}
          </div>
          <div className="flex items-center gap-2">
            <StatusBlock
              type={signatureRequest.status === "completed" ? "success" : "info"}
              label={
                signatureRequest.status === "completed"
                  ? "Terminé"
                  : `${signedCount}/${totalCount} signé${signedCount > 1 ? "s" : ""}`
              }
            />
          </div>
        </div>

        {/* PDF preview */}
        {signatureRequest.current_file?.url && (
          <div className="border rounded-xl overflow-hidden">
            <iframe
              src={signatureRequest.current_file.url}
              className="w-full h-400"
              title="PDF Preview"
            />
          </div>
        )}

        {/* Signers */}
        <div className="flex flex-col gap-2">
          <div className="text-h2 font-medium">Signataires</div>
          {signatureRequest.signers.map((signer) => (
            <div
              key={signer.id}
              className="flex items-center gap-3 p-3 border rounded-xl"
            >
              <div className="flex-1">
                <div className="text-h2 font-medium">{signer.signer_name}</div>
                <div className="text-h3 text-neutral-400">
                  {signer.signer_email}
                </div>
              </div>
              {isSigned(signer.status) ? (
                <StatusBlock
                  type="success"
                  label={signer.status === "signed_in_app" ? "Signé (auto)" : "Signé"}
                />
              ) : (
                <div className="flex items-center gap-2">
                  <StatusBlock type="warning" label="En attente" />
                  {signer.sign_url && (() => {
                    const userEmail = (userData?.infos?.email || "").toLowerCase();
                    const signerEmail = (signer.signer_email || "").toLowerCase();
                    const canSign = !isGuest || (userEmail && signerEmail === userEmail);
                    return canSign ? (
                      <Button
                        variant="small"
                        label="Signer"
                        onClick={() => window.open(signer.sign_url, "_blank")}
                      />
                    ) : null;
                  })()}
                  {!isGuest && (
                    <SecondaryButton
                      variant="small"
                      onClick={() => handleResend(signer.id)}
                      disabled={resendingId === signer.id}
                    >
                      {resendingId === signer.id ? "Envoi..." : "Renvoyer"}
                    </SecondaryButton>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="border-t p-4 flex justify-end">
        <Button variant="small" label="Fermer" onClick={onClose} />
      </div>
    </BaseModal>
  );
};
